import { useQueries, useQuery } from '@tanstack/react-query';
import { getMe, listMembers, type Task } from './api';
import { roleAllows, roleCanClaim, roleReason, type AccessLevel } from './roles';

/**
 * Names for the people holding a set of tasks.
 *
 * `assignee_user_id` is an id, and an id on a row is not an answer to "who has this". Names live
 * on project membership, which is also the only place they *can* live: a person is nameable to
 * you because you share a project with them, not because their id appeared in your payload.
 *
 * Fetched per distinct project among the visible tasks — usually none or one, and React Query
 * caches each. A task outside every project has no assignee chrome at all, so it asks for nothing.
 */
export function useAssignees(tasks: readonly Pick<Task, 'projectId'>[]) {
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });

  const projectIds = [...new Set(tasks.map((t) => t.projectId).filter((id): id is string => !!id))];

  const results = useQueries({
    queries: projectIds.map((id) => ({
      queryKey: ['members', id],
      queryFn: () => listMembers(id),
      // Membership changes rarely and this runs on every list render.
      staleTime: 60_000,
    })),
  });

  const names = new Map<string, string>();
  for (const r of results) {
    for (const m of r.data ?? []) names.set(m.userId, m.displayName || m.email);
  }

  /*
    What the viewer's role lets them do, per project.

    "Take it on" must appear only where it would actually work — an offer that answers with a 403
    is worse than no offer, because the reader has no way to tell whether they mis-clicked or the
    app is broken. Claiming is `assign`, which is developer and above.
  */
  const myRole = new Map<string, string>();
  for (const [i, r] of results.entries()) {
    const projectId = projectIds[i]!;
    const mine = (r.data ?? []).find((m) => m.userId === me.data?.id);
    if (mine && mine.acceptedAt !== null) myRole.set(projectId, mine.role);
  }

  const roleIn = (projectId: string | null) =>
    projectId === null ? null : (myRole.get(projectId) ?? null);

  return {
    myUserId: me.data?.id ?? null,
    /** The name to show, or null when it cannot be resolved yet. */
    nameFor: (userId: string | null) => (userId === null ? null : (names.get(userId) ?? null)),
    /**
     * Whether the viewer may take work on in this project.
     *
     * Unknown while the membership is still loading, and `false` then rather than true: briefly
     * missing a control is a smaller wrong than briefly offering one that fails.
     */
    canClaim: (projectId: string | null) => roleCanClaim(roleIn(projectId)),
    /**
     * Whether the viewer's role passes `level` in this project — the same map, any level.
     *
     * Outside a project there is no role gate, as `useProjectRole.can` says: it is your own work.
     * Inside one, false while the membership loads, for the reason `canClaim` gives.
     */
    can: (projectId: string | null, level: AccessLevel) =>
      projectId === null || roleAllows(roleIn(projectId), level),
    /** The sentence for a control this role cannot use; null outside a project or while loading. */
    why: (projectId: string | null, level: AccessLevel) => roleReason(roleIn(projectId), level),
  };
}
